import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { ErrorState, LoadingState } from "@/components/States";
import { TagsSection } from "@/components/TagsSection";
import { executionsApi, ordersApi } from "@/lib/api";
import { formatDateTime, formatRelative, shortId } from "@/lib/format";
import { OrderStatus, type OrderAdjustmentCreate, type ProductionOrderUpdate } from "@/lib/types";

const ALLOWED: Record<(typeof OrderStatus)[number], readonly (typeof OrderStatus)[number][]> = {
  DRAFT: ["ACTIVE", "CANCELLED"],
  ACTIVE: ["ADJUSTED", "COMPLETED", "CANCELLED"],
  ADJUSTED: ["ACTIVE", "COMPLETED", "CANCELLED"],
  COMPLETED: [],
  CANCELLED: [],
};

export default function OrderDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [adjusting, setAdjusting] = useState(false);

  const order = useQuery({
    queryKey: ["order", id, "detailed"],
    queryFn: () => ordersApi.detailed(id!),
    enabled: !!id,
    refetchInterval: 5000,
  });

  const executions = useQuery({
    queryKey: ["executions", "by-order", id],
    queryFn: () => executionsApi.list({ order_id: id!, page_size: 50 }),
    enabled: !!id,
    refetchInterval: 5000,
  });

  const updateStatus = useMutation({
    mutationFn: (data: ProductionOrderUpdate) => ordersApi.update(id!, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["order", id] }),
  });

  if (!id) return <ErrorState error={new Error("Bad order id")} />;
  if (order.isLoading) return <LoadingState />;
  if (order.isError) return <ErrorState error={order.error} />;

  const o = order.data!;
  const remaining = Math.max(0, o.effective_target - o.completed_quantity);
  const progress = o.effective_target > 0 ? (o.completed_quantity / o.effective_target) * 100 : 0;

  return (
    <>
      <PageHeader
        eyebrow={`order · ${o.code}`}
        title={`STAGE_${o.stage_id}`}
        description={t("orderDetail.headerDescription", {
          created: formatDateTime(o.created_at),
          target: o.target_quantity,
          adjustment: `${o.effective_target - o.target_quantity > 0 ? "+" : ""}${o.effective_target - o.target_quantity}`,
          effective: o.effective_target,
        })}
        actions={<Link to="/orders" className="btn">{t("orderDetail.allOrders")}</Link>}
      />

      <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Stat label={t("orderDetail.statStatus")}><StatusPill kind="order" value={o.status} /></Stat>
        <Stat label={t("orderDetail.statEffective")} value={o.effective_target} />
        <Stat label={t("orderDetail.statDone")} value={o.completed_quantity} />
        <Stat label={t("orderDetail.statRejected")} value={o.rejected_quantity} danger={o.rejected_quantity > 0} />
      </section>

      <section className="card p-5 space-y-3">
        <div className="flex items-center justify-between text-sm">
          <span className="font-mono text-text-muted text-xs uppercase tracking-wider">{t("orderDetail.progress")}</span>
          <span className="font-mono text-text-muted text-xs">
            {o.completed_quantity}/{o.effective_target} · {t("orderDetail.remaining", { remaining })}
          </span>
        </div>
        <div className="h-2 bg-surface-alt rounded-full overflow-hidden">
          <div
            className="h-full bg-accent transition-all"
            style={{ width: `${Math.min(100, progress)}%` }}
          />
        </div>
      </section>

      <section className="card p-5">
        <h2 className="text-base text-text-heading">{t("orderDetail.control")}</h2>
        <div className="mt-4 flex flex-wrap gap-2 items-center">
          {ALLOWED[o.status].length === 0 ? (
            <span className="text-text-muted text-sm">{t("orderDetail.terminalStatus")}</span>
          ) : (
            ALLOWED[o.status].map((target) => (
              <button
                key={target}
                className="btn"
                onClick={() => updateStatus.mutate({ status: target })}
                disabled={updateStatus.isPending}
              >
                → {target}
              </button>
            ))
          )}
          {(o.status === "DRAFT" || o.status === "ACTIVE" || o.status === "ADJUSTED") && (
            <button className="btn btn-primary ml-auto" onClick={() => setAdjusting(true)}>
              {t("orderDetail.addAdjustment")}
            </button>
          )}
        </div>
        {updateStatus.isError && (
          <p className="text-danger text-sm mt-3">
            {updateStatus.error instanceof Error ? updateStatus.error.message : t("common.error")}
          </p>
        )}
      </section>

      <section className="card p-5">
        <h2 className="text-base text-text-heading">{t("orderDetail.adjustmentsTitle")}</h2>
        {o.adjustments.length === 0 ? (
          <p className="text-text-muted text-sm mt-2">{t("orderDetail.noAdjustments")}</p>
        ) : (
          <table className="w-full text-sm mt-4">
            <thead>
              <tr className="text-left text-text-muted font-mono text-xs uppercase">
                <th className="px-2 py-2">{t("orderDetail.colCode")}</th>
                <th className="px-2 py-2">Δ</th>
                <th className="px-2 py-2">{t("orderDetail.colSignals")}</th>
                <th className="px-2 py-2">{t("orderDetail.colReason")}</th>
                <th className="px-2 py-2">{t("orderDetail.colWhen")}</th>
              </tr>
            </thead>
            <tbody>
              {o.adjustments.map((a) => (
                <tr key={a.id} className="border-t border-border">
                  <td className="px-2 py-2 font-mono">{a.code}</td>
                  <td className="px-2 py-2 tabular-nums font-mono">
                    <span className={a.delta_quantity > 0 ? "text-warn" : "text-info"}>
                      {a.delta_quantity > 0 ? "+" : ""}{a.delta_quantity}
                    </span>
                  </td>
                  <td className="px-2 py-2 font-mono text-xs">
                    {a.exception_signals?.join(", ") || "—"}
                  </td>
                  <td className="px-2 py-2 text-text-secondary text-xs">{a.reason ?? "—"}</td>
                  <td className="px-2 py-2 text-text-muted text-xs">{formatRelative(a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card p-5">
        <h2 className="text-base text-text-heading">{t("orderDetail.executionsTitle")}</h2>
        {(executions.data?.items.length ?? 0) === 0 ? (
          <p className="text-text-muted text-sm mt-2">{t("orderDetail.noExecutions")}</p>
        ) : (
          <table className="w-full text-sm mt-4">
            <thead>
              <tr className="text-left text-text-muted font-mono text-xs uppercase">
                <th className="px-2 py-2">Exec</th>
                <th className="px-2 py-2">{t("orderDetail.colOperation")}</th>
                <th className="px-2 py-2">{t("orderDetail.colStation")}</th>
                <th className="px-2 py-2">{t("orderDetail.colStatus")}</th>
                <th className="px-2 py-2">QC</th>
                <th className="px-2 py-2">EXC</th>
                <th className="px-2 py-2">Started</th>
              </tr>
            </thead>
            <tbody>
              {executions.data!.items.map((e) => (
                <tr key={e.id} className="border-t border-border">
                  <td className="px-2 py-2 font-mono text-text-muted">{shortId(e.id)}</td>
                  <td className="px-2 py-2 font-mono text-text-muted">{shortId(e.operation_id)}</td>
                  <td className="px-2 py-2 font-mono text-text-muted">{e.station_id ?? "—"}</td>
                  <td className="px-2 py-2"><StatusPill kind="execution" value={e.status} /></td>
                  <td className="px-2 py-2">{e.qc_result ? <StatusPill kind="qc" value={e.qc_result} /> : <span className="text-text-muted">—</span>}</td>
                  <td className="px-2 py-2 font-mono text-xs text-danger">{e.exception_signal ?? ""}</td>
                  <td className="px-2 py-2 text-text-muted text-xs">{formatRelative(e.started_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <TagsSection
        targetType="order"
        targetId={o.id}
        hint={t("orderDetail.tagsHint")}
      />

      {adjusting && <AdjustModal orderId={id} onClose={() => setAdjusting(false)} />}
    </>
  );
}

function Stat({ label, value, danger, children }: { label: string; value?: string | number; danger?: boolean; children?: React.ReactNode }) {
  return (
    <div className="card p-4">
      <p className="text-xs uppercase tracking-wider font-mono text-text-muted">{label}</p>
      {children ?? (
        <p className={`text-2xl mt-2 tabular-nums font-serif ${danger ? "text-danger" : "text-text-h1"}`}>{value}</p>
      )}
    </div>
  );
}

function AdjustModal({ orderId, onClose }: { orderId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [code, setCode] = useState("POA_manual");
  const [delta, setDelta] = useState(1);
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (data: OrderAdjustmentCreate) => ordersApi.adjust(orderId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["order", orderId] });
      onClose();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : t("common.error")),
  });

  return (
    <div className="fixed inset-0 bg-overlay/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
      <form
        onSubmit={(e) => { e.preventDefault(); setErr(null); if (!code.trim()) { setErr(t("orderDetail.errCodeRequired")); return; } mut.mutate({ code: code.trim(), delta_quantity: delta, reason: reason || null }); }}
        onClick={(e) => e.stopPropagation()}
        className="card p-6 w-full max-w-md space-y-4"
      >
        <h2 className="text-xl">{t("orderDetail.adjustModalTitle")}</h2>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("orderDetail.poaCode")}</span>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border font-mono"
          />
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">Δ (delta_quantity)</span>
          <input
            type="number"
            value={delta}
            onChange={(e) => setDelta(Number(e.target.value))}
            className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border tabular-nums"
          />
          <p className="text-text-muted text-xs mt-1">{t("orderDetail.deltaHint")}</p>
        </label>
        <label className="block">
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{t("orderDetail.reasonLabel")}</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            className="mt-1 w-full px-3 py-2 rounded-md bg-surface border border-border"
          />
        </label>
        {err && <ErrorBanner onDismiss={() => setErr(null)}>{err}</ErrorBanner>}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn" onClick={onClose}>{t("common.cancel")}</button>
          <button type="submit" className="btn btn-primary" disabled={mut.isPending}>
            {mut.isPending ? t("common.creating") : t("common.create")}
          </button>
        </div>
      </form>
    </div>
  );
}
